import { computed, inject, signal } from "@angular/core";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";

import { Subscription } from "rxjs";
import { CanDeactivateClass } from "src/app/shared/classes/can-deactivate.class";
import {
  RecordStateEnum,
  RulesetResourceTypeEnum,
  RouteEnum,
  RulesetConditionOperatorEnum,
} from "src/app/shared/enums";
import {
  ConditionFormValue,
  ConditionGroupFormValue,
  ConditionItemFormValue,
  IConditionAny,
  IConditionEq,
  IConditionGroups,
  IConditionType,
  IConditionValue,
  IDocumentType,
  IExpectation,
  IExpectationItemForm,
  IExpectationItemFormValue,
  ILocationExtended,
  ILocationType,
  IRuleset,
} from "src/app/shared/interfaces";
import {
  CommonService,
  DocumentTypesService,
  LocationsService,
  LocationTypesService,
  RulesetsService,
} from "src/app/shared/services";

import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { TextConstants } from "@shared/constants";
import { NotificationService } from "@shared/services";
import { CommonUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

import { RulesetsBuilderService } from "./rulesets-builder.service";

export abstract class RulesetBaseClass extends CanDeactivateClass {
  public isEditing = signal<boolean>(false);

  public element = signal<IRuleset>(undefined);

  public isLoading = signal(true);

  public formGroup: FormGroup;

  protected documentTypeOptions = signal<InputSelectOption[]>([]);

  public readonly countryOptions = computed(() =>
    this.commonService.countriesOptions().map((country) => ({
      ...country,
      flag: country.value as string,
    })),
  );

  public locationTypeOptions = signal<InputSelectOption[]>([]);

  public locationOptions = signal<InputSelectOption[]>([]);

  public resourceType: RulesetResourceTypeEnum;

  public propertyOptions: InputSelectOption[] = [];

  public readonly resourceTypeEnum = RulesetResourceTypeEnum;

  public readonly routingEnum = RouteEnum;

  public readonly recordStateEnum = RecordStateEnum;

  protected readonly activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  protected readonly hasRulesetsWriteAccess = this.authenticationService.hasRulesetsWriteAccess;

  protected readonly route = inject(ActivatedRoute);

  protected readonly router = inject(Router);

  protected readonly documentTypesService = inject(DocumentTypesService);

  protected readonly notificationService = inject(NotificationService);

  protected readonly commonService = inject(CommonService);

  protected readonly rulesetsService = inject(RulesetsService);

  protected readonly locationsService = inject(LocationsService);

  protected readonly rulesetsBuilderService = inject(RulesetsBuilderService);

  public readonly locationTypesService = inject(LocationTypesService);

  protected formBuilder = inject(FormBuilder);

  protected readonly subscriptions = new Subscription();

  constructor() {
    super();

    this.rulesetsBuilderService.reset();

    this.subscriptions.add(
      this.route.params.subscribe(async (params: Params) => {
        await this.loadData(params["id"]);
      }),
    );
  }

  protected abstract loadData(rulesetId: string): Promise<boolean>;

  protected async getAllDocumentTypes(): Promise<IDocumentType[]> {
    return await this.documentTypesService.getAll();
  }

  protected async getAllLocationTypes(): Promise<ILocationType[]> {
    return await this.locationTypesService.getAll();
  }

  protected async getAllLocations(): Promise<ILocationExtended[]> {
    return await this.locationsService.getAllGraphQL();
  }

  protected async setLocationTypeOptions() {
    const locationTypes = await this.getAllLocationTypes();

    const existingLocationTypeIds = this.rulesetsBuilderService.locationTypeUrisInRuleset.map(
      (uri) => {
        return CommonUtils.getUriId(uri);
      },
    );

    const filteredLocationTypes = locationTypes.filter((lt) => {
      return lt.recordState === RecordStateEnum.ACTIVE || existingLocationTypeIds.includes(lt.id);
    });

    const locationTypesOptions = filteredLocationTypes.map((locationType) => ({
      label: locationType.type,
      value: `/organisations/${this.activeOrganisationId}/location-types/${locationType.id}`,
      icon: locationType.pointOfOrigin ? "target" : undefined,
      iconTooltip: locationType.pointOfOrigin ? TextConstants.POINT_OF_ORIGIN_CHIP : undefined,
    }));

    this.locationTypeOptions.set(locationTypesOptions);
  }

  protected async setLocationOptions() {
    const locations = await this.getAllLocations();

    const existingLocationIds = this.rulesetsBuilderService.locationUrisInRuleset.map((uri) => {
      return CommonUtils.getUriId(uri);
    });

    const filteredLocations = locations.filter((location) => {
      return (
        location.recordState === RecordStateEnum.ACTIVE || existingLocationIds.includes(location.id)
      );
    });

    const locationOptions = filteredLocations.map((location) => ({
      label: location.name,
      value: `/organisations/${this.activeOrganisationId}/locations/${location.id}`,
    }));

    this.locationOptions.set(locationOptions);
  }

  protected async setDocumentTypeOptions() {
    const documentTypes = await this.getAllDocumentTypes();

    const existingDocumentTypeIds = this.rulesetsBuilderService.documentTypeUrisInRuleset.map(
      (uri) => {
        return CommonUtils.getUriId(uri);
      },
    );

    const filteredDocumentTypes = documentTypes.filter((dt) => {
      return dt.recordState === RecordStateEnum.ACTIVE || existingDocumentTypeIds.includes(dt.id);
    });

    this.documentTypeOptions.set(
      filteredDocumentTypes.map((type) => ({
        label: type.name,
        value: `/organisations/${this.activeOrganisationId}/document-types/${type.id}`,
      })),
    );
  }

  private async locationTypeTemporaryHandler(value: InputSelectOption): Promise<string> {
    const newType = await this.locationTypesService.createOrUpdate({
      type: value.label,
      pointOfOrigin: false,
    });

    return `/organisations/${this.activeOrganisationId}/location-types/${newType.id}`;
  }

  private async documentTypeTemporaryHandler(value: InputSelectOption): Promise<string> {
    const newType = await this.documentTypesService.createOrUpdate({
      name: value.label,
    });

    return `/organisations/${this.activeOrganisationId}/document-types/${newType.id}`;
  }

  protected async getConditionPayload(input: ConditionFormValue): Promise<IConditionGroups> {
    if (!input.groupForms.length) {
      return null;
    }

    const transformConditionItemForm = async (
      conditionItemFormValue: ConditionItemFormValue,
    ): Promise<IConditionType> => {
      const operator = conditionItemFormValue.operator;

      const objectValue = {
        path: conditionItemFormValue.path,
        value: await this.transformValue(
          conditionItemFormValue.value,
          this.locationTypeTemporaryHandler.bind(this),
        ),
      };

      if (operator === RulesetConditionOperatorEnum.EQ) {
        return { eq: objectValue };
      }

      if (operator === RulesetConditionOperatorEnum.NONE) {
        return { not: { any: objectValue } };
      }

      return { any: objectValue };
    };

    const transformConditionGroupForm = async (
      conditionGroupFormValue: ConditionGroupFormValue,
    ): Promise<IConditionGroups> => {
      const transformedConditions = await Promise.all(
        conditionGroupFormValue.itemForms.map((form) => transformConditionItemForm(form)),
      );

      return {
        [conditionGroupFormValue.booleanOperator]: transformedConditions,
      };
    };

    const transformedConditions = await Promise.all(
      input.groupForms.map((form) => transformConditionGroupForm(form)),
    );

    return {
      [input.booleanOperator]: transformedConditions,
    };
  }

  protected async transformValue(
    values: ConditionItemFormValue["value"],
    temporaryHandler: (value: InputSelectOption) => Promise<string>,
  ): Promise<string[] | string> {
    const transformedValues: string[] = [];

    const isArray = Array.isArray(values);

    let newValues: string[] | InputSelectOption[] = [];

    if (isArray) {
      newValues = values;
    } else {
      newValues = [values] as string[] | InputSelectOption[];
    }

    for (const item of newValues) {
      if (typeof item === "string") {
        transformedValues.push(item);
      } else if (item.temporary) {
        const newUri = await temporaryHandler(item);

        transformedValues.push(newUri);
      } else {
        transformedValues.push(item.value as string);
      }
    }

    if (!isArray) {
      return transformedValues[0];
    }

    return transformedValues;
  }

  async getExpectationsPayload(expectations: IExpectationItemFormValue[]): Promise<IExpectation[]> {
    return Promise.all(
      expectations.map(async (expectation) => {
        const transformedValue = await this.transformValue(
          expectation.value,
          this.documentTypeTemporaryHandler.bind(this),
        );

        const conditionValue: IConditionValue = {
          path: expectation.pathSuffix,
          value:
            expectation.operator === RulesetConditionOperatorEnum.EQ
              ? (transformedValue as string)
              : (transformedValue as string[]),
        };

        const conditionType: IConditionType =
          expectation.operator === RulesetConditionOperatorEnum.EQ
            ? { eq: conditionValue }
            : { any: conditionValue };

        return {
          path: expectation.pathPrefix,
          condition: {
            and: [conditionType],
          },
        };
      }),
    );
  }

  protected getExpectationsForm(
    expectations: IExpectation[],
    isReadOnly: boolean = false,
  ): FormArray<FormGroup<IExpectationItemForm>> {
    const formGroups: FormGroup<IExpectationItemForm>[] = [];

    for (const expectation of expectations) {
      const conditions = expectation.condition?.and || [];

      for (const condition of conditions) {
        const conditionType = this.getConditionType(expectation);
        const conditionValue = this.getConditionValue(condition, conditionType);

        if (!conditionValue) {
          continue;
        }

        const formGroup = this.createExpectationFormGroup(
          expectation.path,
          conditionValue.path,
          conditionType,
          conditionValue.value,
          isReadOnly,
        );

        formGroups.push(formGroup);
      }
    }

    return this.formBuilder.array(formGroups);
  }

  private getConditionValue(
    condition: IConditionType,
    conditionType: RulesetConditionOperatorEnum,
  ): IConditionValue | null {
    if (conditionType === RulesetConditionOperatorEnum.EQ) {
      return (condition as IConditionEq)?.eq;
    }

    return (condition as IConditionAny)?.any;
  }

  private createExpectationFormGroup(
    path: string,
    pathSuffix: string,
    operator: RulesetConditionOperatorEnum,
    value: string | string[],
    isReadOnly: boolean,
  ): FormGroup<IExpectationItemForm> {
    if (Array.isArray(value)) {
      this.rulesetsBuilderService.documentTypeUrisInRuleset.push(...value);
    } else {
      this.rulesetsBuilderService.documentTypeUrisInRuleset.push(value as string);
    }

    return this.formBuilder.group({
      pathPrefix: [{ value: path, disabled: isReadOnly }, CustomValidators.required],
      pathSuffix: [{ value: pathSuffix, disabled: isReadOnly }, CustomValidators.required],
      operator: [{ value: operator, disabled: isReadOnly }, CustomValidators.required],
      value: [{ value, disabled: isReadOnly }, CustomValidators.required],
    }) as unknown as FormGroup<IExpectationItemForm>;
  }

  protected getExpectationsFormFromLegacy(
    expectations: IExpectation[],
    isReadOnly: boolean = false,
  ): FormArray<FormGroup<IExpectationItemForm>> {
    const formGroups: FormGroup<IExpectationItemForm>[] = [];

    for (const expectation of expectations) {
      const eqCondition = (expectation.condition?.and?.[0] as IConditionEq).eq;

      if (!eqCondition) {
        continue;
      }

      const formGroup = this.createExpectationFormGroup(
        expectation.path,
        eqCondition.path,
        RulesetConditionOperatorEnum.EQ,
        eqCondition.value,
        isReadOnly,
      );

      formGroups.push(formGroup);
    }

    return this.formBuilder.array(formGroups);
  }

  private getConditionType(
    expectation: IExpectation,
  ): RulesetConditionOperatorEnum.ANY | RulesetConditionOperatorEnum.EQ {
    const firstCondition = expectation.condition?.and?.[0];

    if (!firstCondition) {
      return null;
    }

    if ((firstCondition as IConditionAny)?.any) {
      return RulesetConditionOperatorEnum.ANY;
    }
    if ((firstCondition as IConditionEq)?.eq) {
      return RulesetConditionOperatorEnum.EQ;
    }

    return null;
  }

  protected navigateToEditRuleset = async (
    resourceType: RulesetResourceTypeEnum,
    id: string,
    replaceUrl: boolean = false,
  ): Promise<void> => {
    let route: RouteEnum;

    switch (resourceType) {
      case this.resourceTypeEnum.CONNECTION:
        route = this.routingEnum.ADMIN_RULESETS_EDIT_CONNECTION;
        break;
      case this.resourceTypeEnum.LOCATION:
        route = this.routingEnum.ADMIN_RULESETS_EDIT_LOCATION;
        break;
      case this.resourceTypeEnum.DELIVERY:
        route = this.routingEnum.ADMIN_RULESETS_EDIT_DELIVERY;
        break;
    }

    await this.router.navigate([`/${route}/${id}`], { replaceUrl });
  };

  public onCancel(): void {
    const route = this.isEditing()
      ? `/${RouteEnum.ADMIN_RULESETS_DETAILS}/${this.element().id}`
      : `/${RouteEnum.ADMIN_RULESETS}`;

    this.router.navigate([route]);
  }
}
